import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import gql from 'graphql-tag';
import {labelFrag, MultiSelectType} from '../../components/multi-select/multi-select.component';
import {Apollo} from 'apollo-angular';
import {EditTemplateLabelsGet} from './__generated__/EditTemplateLabelsGet';
import {catchError, EMPTY, Subject, Subscription, switchMap} from 'rxjs';
import {ErrorService} from '../../services/error.service';
import {MultiSelectComponentLabelFragment} from '../../components/multi-select/__generated__/MultiSelectComponentLabelFragment';
import {UntypedFormControl} from '@angular/forms';
import {EditTemplateLabels, EditTemplateLabelsVariables} from './__generated__/EditTemplateLabels';
import {ToastrService} from 'ngx-toastr';

const editTemplateLabelsGetQuery = gql`
  query EditTemplateLabelsGet {
    labels {
      edges {
        node {
          ...MultiSelectComponentLabelFragment
        }
      }
    }
  }
  ${labelFrag}
`;

const editTemplateLabelsMutation = gql`
  mutation EditTemplateLabels($id: ID, $labels: [ID]) {
    updateOrCreateBuilderTemplate(input: {id: $id, labels: $labels}) {
      templateItem {
        id
        labels {
          edges {
            node {
              ...MultiSelectComponentLabelFragment
            }
          }
        }
      }
      error
    }
  }
  ${labelFrag}
`;

@Component({
  selector: 'app-edit-template-labels',
  templateUrl: './edit-template-labels.component.html',
  styleUrls: ['./edit-template-labels.component.scss'],
  standalone: false,
})
export class EditTemplateLabelsComponent implements OnInit, OnDestroy {
  @Output() activeLabels: EventEmitter<MultiSelectComponentLabelFragment[]> = new EventEmitter();
  templateItemId = '';
  currentLabels: MultiSelectComponentLabelFragment[] = [];
  labels: MultiSelectComponentLabelFragment[] = [];
  labelControl = new UntypedFormControl();
  MultiSelectType = MultiSelectType;
  saveLabels$ = new Subject<MultiSelectComponentLabelFragment[]>();
  private subscriptions: Subscription[] = [];
  constructor(
    private bsModalRef: BsModalRef,
    private apollo: Apollo,
    private errorService: ErrorService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.apollo
        .watchQuery<EditTemplateLabelsGet>({
          query: editTemplateLabelsGetQuery,
        })
        .valueChanges.pipe(
          catchError(err => {
            this.errorService.handleDefault(err);
            return EMPTY;
          }),
        )
        .subscribe(output => {
          this.labels = (output.data?.labels.edges ?? [])
            .map(e => e.node)
            .filter(e => e.viewAllContent !== true);
          this.labelControl.setValue(this.currentLabels);
        }),
      this.saveLabels$
        .pipe(
          switchMap(labels => {
            return this.apollo
              .mutate<EditTemplateLabels, EditTemplateLabelsVariables>({
                mutation: editTemplateLabelsMutation,
                variables: {
                  id: this.templateItemId,
                  labels: labels.map(l => l.id),
                },
              })
              .pipe(
                catchError(err => {
                  this.errorService.handleDefault(err);
                  return EMPTY;
                }),
              );
          }),
        )
        .subscribe(output => {
          this.toastr.success($localize`Successfully saved`);
          this.activeLabels.emit(this.labelControl.value);
          this.closeModal();
        }),
    );
  }

  closeModal(): void {
    this.bsModalRef.hide();
  }

  saveLabels(): void {
    this.saveLabels$.next(this.labelControl.value);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
